import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { LockKey, LockKeyOpen, Plus, Trash, Archive, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Cierres() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    funcionario_id: "",
    fecha_inicio: (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split("T")[0]; })(),
    fecha_fin: new Date().toISOString().split("T")[0],
    nombre: "",
  });
  const [preview, setPreview] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, f] = await Promise.all([
        api.get("/cierres"),
        api.get("/funcionarios"),
      ]);
      setItems(c.data);
      setFuncionarios(f.data.filter((u) => u.role === "funcionario"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const loadPreview = async () => {
    if (!form.funcionario_id) {
      setPreview(null);
      return;
    }
    try {
      const res = await api.get("/cierres/preview", {
        params: {
          fecha_inicio: form.fecha_inicio,
          fecha_fin: form.fecha_fin,
          funcionario_id: form.funcionario_id,
        },
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setPreview(null);
    }
  };

  useEffect(() => {
    if (open) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.fecha_inicio, form.fecha_fin, form.funcionario_id]);

  const handleCreate = async () => {
    if (!form.funcionario_id) {
      toast.error(t("cierres.selectEmployee"));
      return;
    }
    if (!preview || preview.cantidad_horas === 0) {
      toast.error(t("cierres.noHoursToClose"));
      return;
    }
    if (!window.confirm(t("cierres.confirmClose"))) return;
    try {
      await api.post("/cierres", form);
      toast.success(t("common.success"));
      setOpen(false);
      setPreview(null);
      setForm({ ...form, funcionario_id: "", nombre: "" });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleReopen = async (id) => {
    if (!window.confirm(t("cierres.confirmReopen"))) return;
    try {
      await api.post(`/cierres/${id}/reabrir`);
      toast.success(t("cierres.reopened"));
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("cierres.confirmDelete"))) return;
    await api.delete(`/cierres/${id}`);
    toast.success(t("cierres.deleted"));
    load();
  };

  return (
    <div data-testid="cierres-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("cierres.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("cierres.subtitle")}</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 gap-2" data-testid="new-cierre-btn">
              <Plus size={18} weight="bold" /> {t("cierres.newCierre")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t("cierres.newCierre")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t("cierres.employee")}</Label>
                <Select value={form.funcionario_id} onValueChange={(v) => setForm({ ...form, funcionario_id: v })}>
                  <SelectTrigger data-testid="cierre-employee-select">
                    <SelectValue placeholder={t("cierres.selectEmployee")} />
                  </SelectTrigger>
                  <SelectContent>
                    {funcionarios.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name} (€ {(f.valor_hora || 0).toFixed(2)}/h)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("cierres.from")}</Label>
                  <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} data-testid="cierre-from-input" />
                </div>
                <div>
                  <Label>{t("cierres.to")}</Label>
                  <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} data-testid="cierre-to-input" />
                </div>
              </div>
              <div>
                <Label>{t("cierres.name")}</Label>
                <Input
                  placeholder={`${funcionarios.find((f) => f.id === form.funcionario_id)?.name || ""} · ${form.fecha_inicio} → ${form.fecha_fin}`}
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>

              {/* Preview */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">{t("cierres.preview")}</h3>
                {!preview ? (
                  <div className="text-sm text-slate-400">{t("common.loading")}</div>
                ) : preview.cantidad_horas === 0 ? (
                  <div className="text-sm text-amber-700">{t("cierres.noHoursToClose")}</div>
                ) : (
                  <>
                    <div className="space-y-1 mb-3">
                      {preview.resumen.map((r) => (
                        <div key={r.funcionario_id} className="flex justify-between text-sm">
                          <span className="text-slate-700">{r.funcionario_nombre}</span>
                          <span className="font-medium">
                            {r.total_horas}h × € {r.valor_hora.toFixed(2)} ={" "}
                            <span className="text-green-700 font-bold">€ {r.total_pagar.toFixed(2)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between pt-3 border-t border-slate-300 text-sm">
                      <span className="font-bold">{t("cierres.totalHours")}:</span>
                      <span className="font-bold">{preview.total_horas}h ({preview.cantidad_horas} {t("cierres.records")})</span>
                    </div>
                    <div className="flex justify-between pt-1 text-base">
                      <span className="heading-font font-bold text-red-700">{t("cierres.totalPay")}:</span>
                      <span className="heading-font font-bold text-red-700">€ {preview.total_general.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button
                onClick={handleCreate}
                disabled={!preview || preview.cantidad_horas === 0}
                className="bg-red-600 hover:bg-red-700 text-white gap-1"
                data-testid="confirm-cierre-btn"
              >
                <LockKey size={16} /> {t("cierres.closePeriod")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">{t("cierres.history")}</h2>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <Archive size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("cierres.empty")}
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <Card key={c.id} className="p-5 border-slate-200 card-hover" data-testid={`cierre-${c.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-red-50 text-red-700 flex items-center justify-center">
                    <LockKey size={20} weight="duotone" />
                  </div>
                  <div>
                    <h3 className="heading-font font-semibold text-slate-900">{c.nombre}</h3>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {t("cierres.range")}: {c.fecha_inicio} → {c.fecha_fin} · {c.cantidad_registros} {t("cierres.records")}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {t("cierres.closedOn")}: {new Date(c.fecha_cierre).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[11px] uppercase text-slate-500 font-bold tracking-wider">{t("cierres.totalHours")}</div>
                    <div className="text-lg heading-font font-bold">{c.total_horas}h</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase text-slate-500 font-bold tracking-wider">{t("cierres.totalPay")}</div>
                    <div className="text-lg heading-font font-bold text-red-700">€ {c.total_general.toFixed(2)}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setViewing(c)} data-testid={`view-cierre-${c.id}`}>
                      <Eye size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-amber-700 hover:bg-amber-50"
                      onClick={() => handleReopen(c.id)}
                      data-testid={`reopen-cierre-${c.id}`}
                      title={t("cierres.reopen")}
                    >
                      <LockKeyOpen size={16} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(c.id)} data-testid={`delete-cierre-${c.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              </div>

              <Accordion type="single" collapsible className="mt-3">
                <AccordionItem value="details" className="border-0">
                  <AccordionTrigger className="py-2 text-xs uppercase tracking-wider text-slate-500 hover:no-underline">
                    Detalles
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-1 pt-2">
                      {c.resumen.map((r) => (
                        <div key={r.funcionario_id} className="flex justify-between text-sm py-1 border-b border-slate-100">
                          <span className="text-slate-700">{r.funcionario_nombre}</span>
                          <span className="font-medium">
                            {r.total_horas}h × € {r.valor_hora.toFixed(2)} ={" "}
                            <span className="text-green-700 font-bold">€ {r.total_pagar.toFixed(2)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.nombre}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="text-sm text-slate-500">
                {t("cierres.range")}: {viewing.fecha_inicio} → {viewing.fecha_fin}
              </div>
              {viewing.resumen.map((r) => (
                <div key={r.funcionario_id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="heading-font font-semibold">{r.funcionario_nombre}</h4>
                    <Badge className="bg-green-100 text-green-800 border-0">€ {r.total_pagar.toFixed(2)}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">
                    {r.total_horas}h × € {r.valor_hora.toFixed(2)}/h
                  </div>
                  <div className="space-y-1">
                    {r.registros.map((h) => (
                      <div key={h.id} className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                        <span className="text-slate-600">{h.data} · {h.hora_inicio}—{h.hora_fim} · {h.cliente_nombre}</span>
                        <span className="font-medium">{h.total_horas}h</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-between pt-3 border-t-2 border-red-400">
                <span className="heading-font font-bold text-lg">{t("cierres.totalPay")}:</span>
                <span className="heading-font font-bold text-lg text-red-700">€ {viewing.total_general.toFixed(2)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
